import React, { useState, useEffect, useRef } from 'react';
import Draggable from 'react-draggable';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, IconButton, Tooltip, Typography, Box, Button, CircularProgress,
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { beautifyNumber } from '../../../Utils/FormatNumber';
import AssetService from '../../../Services/assets.service';

const HEADER_SX = { fontWeight: 700, fontSize: 13, backgroundColor: '#BFC8FF', color: '#304FFF', py: 1 };
const CELL_SX = { fontSize: 13, py: 1, px: 1.5 };

const SnapshotViewer = ({ open, onClose, caseId, token }) => {
    const { t } = useTranslation();
    const nodeRef = useRef(null);
    const [snapshots, setSnapshots] = useState([]);
    const [loading, setLoading] = useState(false);

    const fetchSnapshots = async () => {
        if (!caseId || !token) return;
        setLoading(true);
        try {
            const res = await AssetService.GetDepreciationSnapshots(caseId, token);
            setSnapshots(res?.data || []);
        } catch (err) {
            console.error('Failed to fetch snapshots:', err);
            setSnapshots([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open) fetchSnapshots();
    }, [open]);

    const handleDelete = async (year) => {
        const result = await Swal.fire({
            title: t('DeleteSnapshotTitle') || 'מחיקת נתוני פחת',
            html: `<p>${t('DeleteSnapshotConfirm') || 'האם למחוק את נתוני הפחת לשנת'} <strong>${year}</strong>?</p>`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d32f2f',
            cancelButtonColor: '#999',
            confirmButtonText: t('Delete') || 'מחק',
            cancelButtonText: t('Cancel') || 'ביטול',
        });

        if (!result.isConfirmed) return;

        try {
            await AssetService.DeleteDepreciationSnapshot(caseId, year, token);
            await Swal.fire({
                title: t('Deleted') || 'נמחק',
                text: `${t('SnapshotDeletedFor') || 'נתוני הפחת נמחקו לשנת'} ${year}`,
                icon: 'success',
                confirmButtonColor: '#304FFF',
            });
            fetchSnapshots();
        } catch (err) {
            console.error('Delete snapshot error:', err);
            Swal.fire({
                title: t('Error') || 'שגיאה',
                text: t('FailedToDeleteSnapshot') || 'מחיקת נתוני הפחת נכשלה',
                icon: 'error',
                confirmButtonColor: '#d32f2f',
            });
        }
    };

    if (!open) return null;

    return (
        <Draggable handle=".drag-handle-snapshots" nodeRef={nodeRef}>
            <div
                ref={nodeRef}
                style={{
                    position: 'absolute',
                    top: '12%',
                    left: '50%',
                    transform: 'translate(-50%, 0)',
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    padding: '0',
                    width: '650px',
                    maxHeight: '70vh',
                    overflowY: 'auto',
                    zIndex: 1500,
                    boxShadow: '0 8px 32px rgba(48, 79, 255, 0.18), 0 2px 8px rgba(0,0,0,0.10)',
                    border: '1px solid #c5ceff',
                }}
            >
                <Box
                    className="drag-handle-snapshots"
                    sx={{
                        cursor: 'move',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        px: 2.5, py: 1.5,
                        background: 'linear-gradient(135deg, #304FFF 0%, #5c6fff 100%)',
                        borderRadius: '12px 12px 0 0',
                        color: '#fff',
                    }}
                >
                    <Tooltip title={t('Close')}>
                        <IconButton onClick={onClose} sx={{ color: '#fff' }}>
                            <CancelIcon />
                        </IconButton>
                    </Tooltip>
                    <Typography sx={{ fontWeight: 700, fontSize: 16 }}>
                        {t('DepreciationSnapshots') || 'נתוני פחת שנשמרו'}
                    </Typography>
                    <Box sx={{ width: 40 }} />
                </Box>

                <Box sx={{ p: 2.5 }}>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress sx={{ color: '#304FFF' }} />
                        </Box>
                    ) : snapshots.length === 0 ? (
                        <Typography sx={{ textAlign: 'center', py: 4, color: '#999', fontSize: 14 }}>
                            {t('NoSnapshots') || 'אין נתוני פחת שנשמרו'}
                        </Typography>
                    ) : (
                        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '8px' }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={HEADER_SX} align="center">{t('Year') || 'שנה'}</TableCell>
                                        <TableCell sx={HEADER_SX} align="center">{t('AssetsCount') || 'מספר נכסים'}</TableCell>
                                        <TableCell sx={HEADER_SX} align="center">{t('AccumulatedDepreciation') || 'פחת שנצבר'}</TableCell>
                                        <TableCell sx={HEADER_SX} align="center">{t('DepreciationThisYear') || 'פחת השנה'}</TableCell>
                                        <TableCell sx={HEADER_SX} align="center">{t('LastUpdated') || 'עדכון אחרון'}</TableCell>
                                        <TableCell sx={HEADER_SX} align="center">{t('Actions') || 'פעולות'}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {snapshots.map((snap) => (
                                        <TableRow key={snap.year} hover>
                                            <TableCell sx={{ ...CELL_SX, fontWeight: 700, color: '#1a237e' }} align="center">{snap.year}</TableCell>
                                            <TableCell sx={CELL_SX} align="center">{snap.assetCount}</TableCell>
                                            <TableCell sx={{ ...CELL_SX, color: '#1565c0' }} align="center">{beautifyNumber(snap.totalDepreciationUntilNow, 0)}</TableCell>
                                            <TableCell sx={{ ...CELL_SX, color: '#6a1b9a' }} align="center">{beautifyNumber(snap.totalDepreciationThisYear, 0)}</TableCell>
                                            <TableCell sx={{ ...CELL_SX, fontSize: 12, color: '#777' }} align="center">
                                                {snap.lastUpdated ? new Date(snap.lastUpdated).toLocaleDateString('he-IL') : '—'}
                                            </TableCell>
                                            <TableCell sx={CELL_SX} align="center">
                                                <Tooltip title={t('DeleteSnapshot') || 'מחק נתוני פחת'}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleDelete(snap.year)}
                                                        sx={{ color: '#d32f2f' }}
                                                    >
                                                        <DeleteIcon sx={{ fontSize: 18 }} />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Box>
            </div>
        </Draggable>
    );
};

export default SnapshotViewer;
